import { useEffect, useState } from "react";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSun, faArrowsRotate, faUserCheck, faUserXmark,
  faBed, faWrench, faUsers, faTriangleExclamation,
  faBroom, faCalendarDay, faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import DashboardLayout from "../layout/DashboardLayout";
import "./MorningBriefingPage.css";

const API = import.meta.env.VITE_API_URL ?? "http://localhost:5050";

function todayISO() {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth()+1).padStart(2,"0")}-${String(n.getDate()).padStart(2,"0")}`;
}

function fmt(d?: string | null) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
}

interface BriefingData {
  arrivals: any[];
  departures: any[];
  inHouse: number;
  dirtyRooms: any[];
  openMaintenance: any[];
  shiftsOnDuty: any[];
}

export default function MorningBriefingPage() {
  const [data, setData] = useState<BriefingData | null>(null);
  const [loading, setLoading] = useState(true);
  const [lastRefreshed, setLastRefreshed] = useState(new Date());
  const token = localStorage.getItem("ai2_token");
  const headers = { Authorization: `Bearer ${token}` };
  const today = todayISO();

  const load = async () => {
    setLoading(true);
    try {
      const [notifRes, hkRes, maintRes, shiftsRes] = await Promise.allSettled([
        axios.get(`${API}/api/dashboard/notifications?date=${today}`, { headers }),
        axios.get(`${API}/api/housekeeping`, { headers }),
        axios.get(`${API}/api/maintenance`, { headers }),
        axios.get(`${API}/api/shift-notes?date=${today}`, { headers }),
      ]);

      const arrivals   = notifRes.status === "fulfilled" ? (notifRes.value.data.todayArrivals   || []) : [];
      const departures = notifRes.status === "fulfilled" ? (notifRes.value.data.todayDepartures || []) : [];
      const inHouseData = notifRes.status === "fulfilled" ? (notifRes.value.data.inHouseCount ?? 0) : 0;

      const hkRooms   = hkRes.status === "fulfilled"    ? (hkRes.value.data    || []) : [];
      const maintenance = maintRes.status === "fulfilled" ? (maintRes.value.data  || []) : [];
      const shifts    = shiftsRes.status === "fulfilled"  ? (shiftsRes.value.data || []) : [];

      setData({
        arrivals,
        departures,
        inHouse: typeof inHouseData === "number" ? inHouseData : (Array.isArray(inHouseData) ? inHouseData.length : 0),
        dirtyRooms: hkRooms.filter((r: any) => r.status === "dirty" || r.status === "cleaning"),
        openMaintenance: maintenance.filter((m: any) => m.status !== "resolved" && m.status !== "closed"),
        shiftsOnDuty: shifts,
      });
      setLastRefreshed(new Date());
    } catch {
      // silently fail — partial data already set
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const dateLabel = new Date().toLocaleDateString("en-GB", {
    weekday: "long", day: "2-digit", month: "long", year: "numeric",
  });

  return (
    <DashboardLayout>
      <div className="mb-page">

        {/* Header */}
        <div className="mb-header">
          <div className="mb-header-left">
            <div className="mb-sun-icon"><FontAwesomeIcon icon={faSun} /></div>
            <div>
              <h1 className="mb-title">Morning Briefing</h1>
              <p className="mb-date">
                <FontAwesomeIcon icon={faCalendarDay} /> {dateLabel}
              </p>
            </div>
          </div>
          <div className="mb-header-right">
            <span className="mb-refreshed">
              Last refreshed {lastRefreshed.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}
            </span>
            <button className="mb-refresh-btn" onClick={load} disabled={loading}>
              <FontAwesomeIcon icon={faArrowsRotate} spin={loading} /> Refresh
            </button>
          </div>
        </div>

        {loading && !data ? (
          <div className="mb-loading">
            <FontAwesomeIcon icon={faSpinner} spin /> Loading briefing…
          </div>
        ) : (
          <>
            {/* KPI row */}
            <div className="mb-kpi-row">
              <div className="mb-kpi mb-kpi-green">
                <FontAwesomeIcon icon={faUserCheck} className="mb-kpi-icon" />
                <div className="mb-kpi-num">{data?.arrivals.length ?? 0}</div>
                <div className="mb-kpi-label">Arrivals Today</div>
              </div>
              <div className="mb-kpi mb-kpi-blue">
                <FontAwesomeIcon icon={faUserXmark} className="mb-kpi-icon" />
                <div className="mb-kpi-num">{data?.departures.length ?? 0}</div>
                <div className="mb-kpi-label">Departures Today</div>
              </div>
              <div className="mb-kpi mb-kpi-navy">
                <FontAwesomeIcon icon={faUsers} className="mb-kpi-icon" />
                <div className="mb-kpi-num">{data?.inHouse ?? 0}</div>
                <div className="mb-kpi-label">In House</div>
              </div>
              <div className="mb-kpi mb-kpi-amber">
                <FontAwesomeIcon icon={faBroom} className="mb-kpi-icon" />
                <div className="mb-kpi-num">{data?.dirtyRooms.length ?? 0}</div>
                <div className="mb-kpi-label">Rooms Need Cleaning</div>
              </div>
              <div className="mb-kpi mb-kpi-red">
                <FontAwesomeIcon icon={faWrench} className="mb-kpi-icon" />
                <div className="mb-kpi-num">{data?.openMaintenance.length ?? 0}</div>
                <div className="mb-kpi-label">Open Maintenance</div>
              </div>
            </div>

            <div className="mb-grid">
              {/* Arrivals */}
              <Section
                title="Arrivals"
                icon={faUserCheck}
                count={data?.arrivals.length}
                color="#34d399"
                empty="No arrivals today"
              >
                {data?.arrivals.map((b, i) => (
                  <div key={b.bookingId || i} className="mb-list-row">
                    <div className="mb-list-avatar" style={{ background: "rgba(52,211,153,0.12)", color: "#34d399" }}>
                      {(b.guestName || "?")[0]}
                    </div>
                    <div className="mb-list-info">
                      <span className="mb-list-name">{b.guestName || "—"}</span>
                      <span className="mb-list-sub">Room {b.roomNumber || b.roomId || "—"} · {b.nights} night{b.nights !== 1 ? "s" : ""}</span>
                    </div>
                    {b.bookingType && b.bookingType !== "Standard" && (
                      <span className="mb-list-tag">{b.bookingType}</span>
                    )}
                  </div>
                ))}
              </Section>

              {/* Departures */}
              <Section
                title="Departures"
                icon={faUserXmark}
                count={data?.departures.length}
                color="#60a5fa"
                empty="No departures today"
              >
                {data?.departures.map((b, i) => (
                  <div key={b.bookingId || i} className="mb-list-row">
                    <div className="mb-list-avatar" style={{ background: "rgba(96,165,250,0.12)", color: "#60a5fa" }}>
                      {(b.guestName || "?")[0]}
                    </div>
                    <div className="mb-list-info">
                      <span className="mb-list-name">{b.guestName || "—"}</span>
                      <span className="mb-list-sub">Room {b.roomNumber || b.roomId || "—"} · Check-out {fmt(b.checkOut)}</span>
                    </div>
                    {b.total > 0 && (
                      <span className="mb-list-tag mb-tag-amber">EGP {Number(b.total).toLocaleString()}</span>
                    )}
                  </div>
                ))}
              </Section>

              {/* Dirty Rooms */}
              <Section
                title="Rooms Needing Cleaning"
                icon={faBroom}
                count={data?.dirtyRooms.length}
                color="#fbbf24"
                empty="All rooms are clean"
              >
                {data?.dirtyRooms.map((r, i) => (
                  <div key={r.roomId || i} className="mb-list-row">
                    <div className="mb-list-avatar" style={{ background: "rgba(251,191,36,0.12)", color: "#fbbf24" }}>
                      <FontAwesomeIcon icon={faBed} />
                    </div>
                    <div className="mb-list-info">
                      <span className="mb-list-name">Room {r.roomNumber}</span>
                      <span className="mb-list-sub">{r.roomType || "—"}{r.assignedTo ? ` · Assigned: ${r.assignedTo}` : ""}</span>
                    </div>
                    <span className={`mb-list-status mb-status-${r.status}`}>{r.status}</span>
                  </div>
                ))}
              </Section>

              {/* Open Maintenance */}
              <Section
                title="Open Maintenance Tickets"
                icon={faWrench}
                count={data?.openMaintenance.length}
                color="#f87171"
                empty="No open maintenance tickets"
              >
                {data?.openMaintenance.slice(0, 10).map((m, i) => (
                  <div key={m.id || i} className="mb-list-row">
                    <div className="mb-list-avatar" style={{ background: "rgba(248,113,113,0.12)", color: "#f87171" }}>
                      <FontAwesomeIcon icon={faTriangleExclamation} />
                    </div>
                    <div className="mb-list-info">
                      <span className="mb-list-name">{m.title || m.issue || "Maintenance issue"}</span>
                      <span className="mb-list-sub">Room {m.room_number || m.roomnumber || "—"} · {m.priority || "normal"}</span>
                    </div>
                    <span className={`mb-list-status mb-maint-${m.status || "open"}`}>{m.status || "open"}</span>
                  </div>
                ))}
              </Section>
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}

function Section({
  title, icon, count, color, empty, children,
}: {
  title: string;
  icon: any;
  count?: number;
  color: string;
  empty: string;
  children?: React.ReactNode;
}) {
  return (
    <div className="mb-section">
      <div className="mb-section-header" style={{ borderLeftColor: color }}>
        <FontAwesomeIcon icon={icon} style={{ color }} />
        <span className="mb-section-title">{title}</span>
        <span className="mb-section-count" style={{ background: `${color}18`, color }}>{count ?? 0}</span>
      </div>
      <div className="mb-section-body">
        {!children || (Array.isArray(children) && children.length === 0) ? (
          <div className="mb-empty">{empty}</div>
        ) : (
          children
        )}
      </div>
    </div>
  );
}
